import { getApiError } from '../../utils/errorHelper';
import React, { useState, useEffect } from 'react';
import { Mail, Phone, Building2, ChevronRight, Search, User, Calendar, Shield, Pencil, X, Save, Loader2, Clock, Key, CheckCircle, XCircle, Trash2, Bell, BellOff } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { adminApi, directorsApi, companiesApi } from '../../services/api';
import { KYCDocuments } from '../KYCDocuments';
import ExternalCredentials from '../ExternalCredentials';
import { entityConfig } from '../../utils/entityTypeConfig';

// Auto-compute expiry status from a date string — never trust stored status
const computeExpiryStatus = (expiryDateStr) => {
  if (!expiryDateStr) return { status: 'unknown', label: 'Unknown', bg: 'bg-slate-500/20', text: 'text-slate-400' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDateStr);
  expiry.setHours(0, 0, 0, 0);
  if (expiry < today) return { status: 'expired', label: 'Expired', bg: 'bg-red-500/20', text: 'text-red-400' };
  const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (daysLeft <= 30) return { status: 'expiring_soon', label: `Expiring (${daysLeft}d)`, bg: 'bg-amber-500/20', text: 'text-amber-400' };
  return { status: 'active', label: 'Active', bg: 'bg-emerald-500/20', text: 'text-emerald-400' };
};

// Computed Status Badge — derives status from expiry date, never stored
const ComputedStatusBadge = ({ expiryDate, label }) => {
  const info = computeExpiryStatus(expiryDate);
  return (
    <div className="flex items-center gap-2" data-testid={`computed-status-${label?.toLowerCase()}`}>
      <span className={`text-xs px-2 py-1 rounded-full ${info.bg} ${info.text}`}>{info.label}</span>
    </div>
  );
};

// Status Badge Component - Reusable for DIN, DSC, etc.
const StatusBadge = ({ status, type }) => {
  const configs = {
    din: {
      active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
      not_applied: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Not Applied' },
      deactivated: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Deactivated' },
    },
    dsc: {
      active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
      applied: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Applied' },
      not_applied: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Not Applied' },
      expired: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Expired' },
    },
    signatory: {
      true: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Yes' },
      false: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'No' },
    }
  };
  
  const config = configs[type]?.[String(status)] || configs[type]?.['not_applied'] || { bg: 'bg-slate-500/20', text: 'text-slate-400', label: status || 'N/A' };
  
  return (
    <span className={`text-xs px-2 py-1 rounded-full ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Display Field Component - Shows value or hides if empty (based on hideIfEmpty prop)
const DisplayField = ({ label, value, mono = false, hideIfEmpty = true }) => {
  if (hideIfEmpty && !value) return null;
  
  return (
    <div className="bg-slate-900/50 rounded-lg p-3">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className={`text-white text-sm ${mono ? 'font-mono' : ''}`}>{value || 'Not provided'}</p>
    </div>
  );
};

// Summary Panel Component for Edit View
const SummaryPanel = ({ companiesCount = 0, credentialsCount = 0, documentsCount = 0 }) => (
  <div className="bg-slate-900/50 rounded-lg p-4 mb-4 grid grid-cols-3 gap-4 border border-slate-700/50">
    <div className="text-center">
      <p className="text-2xl font-bold text-blue-400">{companiesCount}</p>
      <p className="text-xs text-slate-500">Linked Companies</p>
    </div>
    <div className="text-center border-x border-slate-700/50">
      <p className="text-2xl font-bold text-amber-400">{credentialsCount}</p>
      <p className="text-xs text-slate-500">Credentials</p>
    </div>
    <div className="text-center">
      <p className="text-2xl font-bold text-emerald-400">{documentsCount}</p>
      <p className="text-xs text-slate-500">Documents</p>
    </div>
  </div>
);

// Last Updated Badge Component
const LastUpdatedBadge = ({ updatedAt, updatedByName, updatedByRole }) => {
  if (!updatedAt) return null;
  
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  
  const getRoleBadge = (role) => {
    if (role === 'super_admin' || role === 'admin') {
      return { text: 'Admin', color: 'bg-purple-500/20 text-purple-400' };
    }
    return { text: 'Client', color: 'bg-blue-500/20 text-blue-400' };
  };
  
  const roleBadge = getRoleBadge(updatedByRole);
  
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-900/50 rounded-lg px-3 py-2 mt-4">
      <Clock className="w-3.5 h-3.5" />
      <span>Last updated {formatDate(updatedAt)}</span>
      {updatedByName && (
        <>
          <span>by</span>
          <span className="font-medium text-slate-400">{updatedByName}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs ${roleBadge.color}`}>
            {roleBadge.text}
          </span>
        </>
      )}
    </div>
  );
};

const DirectorCard = ({ director, onClick, isSelected }) => {
  // Check if director is from US company
  const isUSDirector = director?.country === 'US' || director?.ssn || director?.itin;

  return (
    <div 
      onClick={onClick}
      className={`bg-slate-800/50 border rounded-xl p-5 cursor-pointer transition-all duration-300 hover:border-indigo-500/50 hover:bg-slate-800 ${
        isSelected ? 'border-indigo-500 ring-2 ring-indigo-500/20' : 'border-slate-700/50'
      }`}
      data-testid={`director-card-${director.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0 ${
          isUSDirector ? 'bg-gradient-to-br from-red-400 to-blue-500' : 'bg-gradient-to-br from-emerald-400 to-teal-500'
        }`}>
          {director.full_name?.charAt(0)?.toUpperCase() || 'D'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-white font-semibold truncate">{director.full_name || 'Unknown'}</h3>
            {/* Country badge */}
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              isUSDirector ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
            }`}>
              {isUSDirector ? 'USA' : 'India'}
            </span>
            {director.is_authorized_signatory && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                Signatory
              </span>
            )}
          </div>
          <p className="text-slate-400 text-sm">{director.designation || (isUSDirector ? 'Member/Manager' : 'Director')}</p>
          <div className="flex items-center gap-2 text-slate-500 text-sm mt-2">
            <Mail className="w-4 h-4" />
            <span className="truncate">{director.email}</span>
          </div>
          {/* US-specific: Ownership */}
          {isUSDirector && director.ownership_percentage && (
            <div className="text-xs text-emerald-400 mt-1">
              Ownership: {director.ownership_percentage}%
            </div>
          )}
          {/* India-specific: DIN */}
          {!isUSDirector && director.din_number && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-slate-500 font-mono">DIN: {director.din_number}</span>
            </div>
          )}

          {/* India-specific: DSC auto-computed status */}
          {!isUSDirector && (director.dsc_expiry_date || director.dsc_expiry) && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-slate-500">DSC:</span>
              <ComputedStatusBadge expiryDate={director.dsc_expiry_date || director.dsc_expiry} label="DSC" />
            </div>
          )}
        </div>
        <ChevronRight className="w-5 h-5 text-slate-500" />
      </div>

      {director.companies_count > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center gap-2 text-sm">
          <Building2 className="w-4 h-4 text-slate-500" />
          <span className="text-slate-400">{director.companies_count} {director.companies_count === 1 ? 'Company' : 'Companies'}</span>
        </div>
      )}
    </div>
  );
};

const DirectorDetailPanel = ({ director, companies, currentUser, onRefresh, onNavigateToCompany, onNavigateToShareholder }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assocMode, setAssocMode] = useState(null); // 'de' | 'add' | null
  const [assocSel, setAssocSel] = useState(() => new Set());
  const [allCompanies, setAllCompanies] = useState([]);
  const [assocBusy, setAssocBusy] = useState(false);
  const [dscReminderBusy, setDscReminderBusy] = useState(false);
  const canManage = ['admin', 'super_admin'].includes(currentUser?.role);

  const dscRemindersOn = director?.dsc_reminders_enabled !== false;
  const toggleDscReminders = async () => {
    setDscReminderBusy(true);
    try {
      const next = !dscRemindersOn;
      await directorsApi.setDscReminders(director.id, next);
      toast.success(next ? 'DSC reminder emails resumed' : 'DSC reminder emails stopped');
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(getApiError(e) || 'Failed to update DSC reminders');
    } finally {
      setDscReminderBusy(false);
    }
  };

  const openDeassoc = () => { setAssocSel(new Set()); setAssocMode('de'); };
  const openAssoc = async () => {
    setAssocSel(new Set()); setAssocMode('add');
    try {
      const list = await companiesApi.getAll();
      setAllCompanies(Array.isArray(list) ? list : (list?.companies || []));
    } catch { setAllCompanies([]); toast.error('Failed to load companies'); }
  };
  const linkedIds = new Set((companies || []).map((c) => c.id));
  const addableCompanies = (allCompanies || []).filter((c) => !linkedIds.has(c.id));
  const assocRows = assocMode === 'de' ? (companies || []) : addableCompanies;
  const toggleAssoc = (id) => setAssocSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAllAssoc = () => setAssocSel((p) => {
    const all = assocRows.map((c) => c.id);
    return all.every((id) => p.has(id)) ? new Set() : new Set(all);
  });
  const runAssoc = async () => {
    if (assocSel.size === 0) { toast.error('Select at least one company'); return; }
    setAssocBusy(true);
    let ok = 0, fail = 0;
    for (const cid of assocSel) {
      try {
        if (assocMode === 'de') await directorsApi.deassociate(cid, director.id);
        else await directorsApi.associate(cid, director.id);
        ok += 1;
      } catch { fail += 1; }
    }
    setAssocBusy(false);
    setAssocMode(null);
    toast[fail ? 'warning' : 'success'](
      `${assocMode === 'de' ? 'De-associated' : 'Associated'} ${ok} compan${ok === 1 ? 'y' : 'ies'}${fail ? ` · ${fail} failed` : ''}`);
    if (onRefresh) onRefresh();
  };

  // Reset form when director changes
  useEffect(() => {
    if (director) {
      setIsEditing(false);
      setShowDeleteConfirm(false);
    }
  }, [director?.id]);

  // Check if director belongs to a US company
  const isUSDirector = director?.country === 'US' || 
    companies?.some(c => c.country === 'US' || c.entity_type?.startsWith('us_')) ||
    director?.ssn || director?.itin;

  // Profit & Loss Sharing Ratio is only meaningful for LLP / Partnership
  // partners. Show it whenever ANY linked company qualifies.
  const hasProfitShareCompany = !!companies?.some(
    (c) => entityConfig(c.entity_type)?.has_profit_share
  );

  const handleEdit = () => {
    // Base fields common to both
    const baseFields = {
      full_name: director?.full_name || '',
      email: director?.email || '',
      phone: director?.phone || '',
      date_of_birth: director?.date_of_birth || '',
      address: director?.address || '',
      is_authorized_signatory: director?.is_authorized_signatory || false,
      nationality: director?.nationality || (isUSDirector ? 'US' : 'Indian'),
      occupation: director?.occupation || '',
    };

    if (isUSDirector) {
      // US-specific fields
      setEditForm({
        ...baseFields,
        ssn: director?.ssn || '',
        itin: director?.itin || '',
        itin_assigned: director?.itin_assigned || false,
        ownership_percentage: director?.ownership_percentage || '',
        compensation_method: director?.compensation_method || '',
        visited_us_for_business: director?.visited_us_for_business || false,
        w8_provided: director?.w8_provided || false,
        us_address_line1: director?.us_address_line1 || '',
        us_address_line2: director?.us_address_line2 || '',
        us_city: director?.us_city || '',
        us_state: director?.us_state || '',
        us_zip: director?.us_zip || '',
      });
    } else {
      // India-specific fields
      setEditForm({
        ...baseFields,
        pan_number: director?.pan_number || '',
        aadhaar_number: director?.aadhaar_number || '',
        din_number: director?.din_number || '',
        kyc_expiry_date: director?.kyc_expiry_date || '',
        dsc_expiry_date: director?.dsc_expiry_date || '',
        dsc_class: director?.dsc_class || '',
        dsc_provider: director?.dsc_provider || '',
        father_name: director?.father_name || '',
        duration_of_stay: director?.duration_of_stay || '',
        v3_username: director?.v3_username || '',
        v3_password: director?.v3_password || '',
        v3_hint_question: director?.v3_hint_question || '',
        profit_share_ratio: director?.profit_share_ratio ?? '',
      });
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let updatePayload = {
        full_name: editForm.full_name,
        email: editForm.email,
        phone: editForm.phone,
        date_of_birth: editForm.date_of_birth || null,
        address: editForm.address,
        is_authorized_signatory: editForm.is_authorized_signatory,
        nationality: editForm.nationality,
        occupation: editForm.occupation || null,
      };

      if (isUSDirector) {
        // US-specific fields
        updatePayload = {
          ...updatePayload,
          ssn: editForm.ssn || null,
          itin: editForm.itin || null,
          itin_assigned: editForm.itin_assigned || false,
          ownership_percentage: editForm.ownership_percentage ? parseFloat(editForm.ownership_percentage) : null,
          compensation_method: editForm.compensation_method || null,
          visited_us_for_business: editForm.visited_us_for_business || false,
          w8_provided: editForm.w8_provided || false,
          us_address_line1: editForm.us_address_line1 || null,
          us_address_line2: editForm.us_address_line2 || null,
          us_city: editForm.us_city || null,
          us_state: editForm.us_state || null,
          us_zip: editForm.us_zip || null,
          country: 'US',
        };
      } else {
        // India-specific fields
        updatePayload = {
          ...updatePayload,
          pan_number: editForm.pan_number,
          aadhaar_number: editForm.aadhaar_number,
          din_number: editForm.din_number,
          kyc_expiry_date: editForm.kyc_expiry_date || null,
          dsc_expiry_date: editForm.dsc_expiry_date || null,
          dsc_expiry: editForm.dsc_expiry_date || null,
          dsc_class: editForm.dsc_class || null,
          dsc_provider: editForm.dsc_provider || null,
          father_name: editForm.father_name,
          duration_of_stay: editForm.duration_of_stay || null,
          v3_username: editForm.v3_username || null,
          v3_password: editForm.v3_password || null,
          v3_hint_question: editForm.v3_hint_question || null,
          profit_share_ratio:
            editForm.profit_share_ratio === '' || editForm.profit_share_ratio == null
              ? null
              : parseFloat(editForm.profit_share_ratio),
          country: 'IN',
        };
      }

      await adminApi.updateDirector(director.id, updatePayload);
      toast.success(isUSDirector ? 'Founder/Member updated successfully' : 'Director updated successfully');
      setIsEditing(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { directorsApi } = await import('../../services/api');
      await directorsApi.delete(director.id);
      toast.success(`${isUSDirector ? 'Founder/Member' : 'Director'} "${director.full_name}" deleted successfully`);
      setShowDeleteConfirm(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete'));
    } finally {
      setDeleting(false);
    }
  };

  if (!director) {
    return (
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-8 flex flex-col items-center justify-center text-center h-96">
        <User className="w-16 h-16 text-slate-600 mb-4" />
        <p className="text-slate-400 text-lg">Select a {isUSDirector ? 'founder/member' : 'director'} to view details</p>
        <p className="text-slate-500 text-sm mt-1">Click on any card to see their information</p>
      </div>
    );
  }

  // Calculate summary counts
  const companiesCount = companies?.length || director?.companies_count || 0;
  const hasV3Credentials = director?.v3_username || director?.v3_password;
  const credentialsCount = hasV3Credentials ? 1 : 0;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
      {/* Director Header */}
      <div className={`p-6 border-b border-slate-700/50 ${
        isUSDirector ? 'bg-gradient-to-r from-red-600/20 to-blue-600/20' : 'bg-gradient-to-r from-emerald-600/20 to-teal-600/20'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl ${
              isUSDirector ? 'bg-gradient-to-br from-red-400 to-blue-500' : 'bg-gradient-to-br from-emerald-400 to-teal-500'
            }`}>
              {director.full_name?.charAt(0)?.toUpperCase() || 'D'}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-white">{director.full_name}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  isUSDirector ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                }`}>
                  {isUSDirector ? 'USA' : 'India'}
                </span>
              </div>
              <p className="text-slate-400">{director.designation || (isUSDirector ? 'Member/Manager' : 'Director')}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {director.is_authorized_signatory && (
                  <span className="text-xs px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                    Authorized Signatory
                  </span>
                )}
                {/* India-specific: DIN */}
                {!isUSDirector && director.din_number && (
                  <span className="text-xs px-3 py-1 rounded-full bg-slate-700 text-slate-300 font-mono">
                    DIN: {director.din_number}
                  </span>
                )}
                {/* US-specific: Ownership */}
                {isUSDirector && director.ownership_percentage && (
                  <span className="text-xs px-3 py-1 rounded-full bg-emerald-700 text-emerald-300">
                    {director.ownership_percentage}% Ownership
                  </span>
                )}
              </div>
            </div>
          </div>
          {!isEditing ? (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleEdit} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                <Pencil className="w-4 h-4 mr-2" /> Edit
              </Button>
              {canManage && (
                <>
                  <Button variant="outline" size="sm" data-testid="deassociate-director-btn"
                    onClick={openDeassoc}
                    className="border-amber-600/50 text-amber-400 hover:bg-amber-600/20 hover:text-amber-300">
                    <X className="w-4 h-4 mr-2" /> De-associate
                  </Button>
                  <Button variant="outline" size="sm" data-testid="associate-director-btn"
                    onClick={openAssoc}
                    className="border-emerald-600/50 text-emerald-400 hover:bg-emerald-600/20 hover:text-emerald-300">
                    <Building2 className="w-4 h-4 mr-2" /> Associate
                  </Button>
                </>
              )}
              {currentUser?.role === 'super_admin' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  data-testid="delete-director-btn"
                  onClick={() => setShowDeleteConfirm(true)} 
                  className="border-red-600/50 text-red-400 hover:bg-red-600/20 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} className="text-slate-400">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
              </Button>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="mt-4 p-4 bg-red-900/30 border border-red-500/50 rounded-lg">
            <p className="text-red-300 text-sm mb-3">
              Are you sure you want to delete <strong>{director.full_name}</strong>? This will also remove them from all associated companies.
            </p>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                onClick={handleDelete} 
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Yes, Delete
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowDeleteConfirm(false)}
                className="text-slate-400 hover:text-slate-300"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Associate / De-associate modal */}
      {assocMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="director-assoc-modal">
          <div className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded-xl border border-slate-700 bg-slate-900 p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-semibold text-white">
                {assocMode === 'de' ? 'De-associate from companies' : 'Associate to companies'}
              </h3>
              <button onClick={() => setAssocMode(null)} data-testid="assoc-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[12px] text-slate-400 mb-3">
              {assocMode === 'de'
                ? <><strong className="text-slate-200">{director.full_name}</strong> stays in the system — only the selected company links are removed. No director data is lost.</>
                : <>Link <strong className="text-slate-200">{director.full_name}</strong> to the selected companies.</>}
            </p>
            {assocRows.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">
                {assocMode === 'de' ? 'Not linked to any company yet.' : 'Already linked to every company.'}
              </p>
            ) : (
              <>
                <label className="flex items-center gap-2 px-2 py-1.5 mb-1 text-[12px] text-slate-300 cursor-pointer">
                  <input type="checkbox" data-testid="assoc-select-all"
                    checked={assocRows.every((c) => assocSel.has(c.id))}
                    onChange={toggleAllAssoc} className="accent-emerald-500" />
                  Select all ({assocRows.length})
                </label>
                <div className="space-y-1 border-t border-slate-800 pt-1">
                  {assocRows.map((c) => (
                    <label key={c.id} data-testid={`assoc-row-${c.id}`}
                      className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-slate-800/60 cursor-pointer">
                      <input type="checkbox" checked={assocSel.has(c.id)} onChange={() => toggleAssoc(c.id)} className="accent-emerald-500" />
                      <span className="text-sm text-white truncate">{c.company_name || c.name}</span>
                      {c.gstin && <span className="text-[10px] font-mono text-slate-500 ml-auto">{c.gstin}</span>}
                    </label>
                  ))}
                </div>
              </>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => setAssocMode(null)} className="text-slate-400">Cancel</Button>
              <Button size="sm" onClick={runAssoc} disabled={assocBusy || assocSel.size === 0} data-testid="assoc-confirm"
                className={assocMode === 'de' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}>
                {assocBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                {assocMode === 'de' ? `De-associate (${assocSel.size})` : `Associate (${assocSel.size})`}
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="p-6 space-y-6 max-h-[calc(100vh-400px)] overflow-y-auto">
        {isEditing ? (
          <div className="space-y-4">
            {/* Summary Panel in Edit Mode */}
            <SummaryPanel 
              companiesCount={companiesCount}
              credentialsCount={credentialsCount}
              documentsCount={0}
            />

            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Full Name *</label>
                <Input value={editForm.full_name} onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Email *</label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Phone</label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" placeholder={isUSDirector ? "+1 555-123-4567" : "+91 9876543210"} />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Date of Birth</label>
                <Input type="date" value={editForm.date_of_birth} onChange={(e) => setEditForm({...editForm, date_of_birth: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" />
              </div>
              
              {/* India-specific: Father's Name */}
              {!isUSDirector && (
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Father's Name</label>
                  <Input value={editForm.father_name || ''} onChange={(e) => setEditForm({...editForm, father_name: e.target.value})}
                    className="bg-slate-900 border-slate-700 text-white" />
                </div>
              )}
              
              {/* US-specific: Ownership percentage */}
              {isUSDirector && (
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Ownership Percentage</label>
                  <Input type="number" min="0" max="100" value={editForm.ownership_percentage || ''} 
                    onChange={(e) => setEditForm({...editForm, ownership_percentage: e.target.value})}
                    className="bg-slate-900 border-slate-700 text-white" placeholder="e.g., 50" />
                </div>
              )}
              
              <div className={isUSDirector ? "col-span-2" : ""}>
                <label className="text-xs text-slate-500 mb-1 block">Address</label>
                <Input value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white" />
              </div>
            </div>

            {/* US-specific: Identity & Tax Details */}
            {isUSDirector ? (
              <>
                <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider pt-4">Identity & Tax Details</h3>
                <div className="grid grid-cols-2 gap-4 bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">SSN (Last 4 digits only)</label>
                    <Input value={editForm.ssn || ''} onChange={(e) => setEditForm({...editForm, ssn: e.target.value})}
                      className="bg-slate-900 border-slate-700 text-white font-mono" placeholder="****" maxLength={4} />
                  </div>
                  <div className="flex items-center gap-3 bg-slate-900/50 rounded-lg p-3">
                    <input type="checkbox" id="itin_assigned" checked={editForm.itin_assigned || false} 
                      onChange={(e) => setEditForm({...editForm, itin_assigned: e.target.checked})}
                      className="w-5 h-5 rounded bg-slate-800 border-slate-600" />
                    <label htmlFor="itin_assigned" className="text-sm text-slate-300">ITIN has been assigned</label>
                  </div>
                  {editForm.itin_assigned && (
                    <div className="col-span-2">
                      <label className="text-xs text-slate-500 mb-1 block">ITIN Number</label>
                      <Input value={editForm.itin || ''} onChange={(e) => setEditForm({...editForm, itin: e.target.value})}
                        className="bg-slate-900 border-slate-700 text-white font-mono" placeholder="9XX-XX-XXXX" />
                    </div>
                  )}
                </div>

                <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider pt-4">Business & Compliance</h3>
                <div className="grid grid-cols-2 gap-4 bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Compensation Method</label>
                    <select value={editForm.compensation_method || ''} onChange={(e) => setEditForm({...editForm, compensation_method: e.target.value})}
                      className="w-full rounded-md bg-slate-900 border-slate-700 text-white p-2 text-sm">
                      <option value="">Select Method</option>
                      <option value="salary">Salary</option>
                      <option value="distributions">Distributions</option>
                      <option value="guaranteed_payments">Guaranteed Payments</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3 bg-slate-900/50 rounded-lg p-3">
                    <input type="checkbox" id="visited_us" checked={editForm.visited_us_for_business || false} 
                      onChange={(e) => setEditForm({...editForm, visited_us_for_business: e.target.checked})}
                      className="w-5 h-5 rounded bg-slate-800 border-slate-600" />
                    <label htmlFor="visited_us" className="text-sm text-slate-300">Visited US for Business</label>
                  </div>
                  <div className="col-span-2 flex items-center gap-3 bg-slate-900/50 rounded-lg p-3">
                    <input type="checkbox" id="w8_provided" checked={editForm.w8_provided || false} 
                      onChange={(e) => setEditForm({...editForm, w8_provided: e.target.checked})}
                      className="w-5 h-5 rounded bg-slate-800 border-slate-600" />
                    <label htmlFor="w8_provided" className="text-sm text-slate-300">W-8 Form Provided</label>
                  </div>
                </div>

                <div className="col-span-2 flex items-center gap-3 bg-emerald-900/30 rounded-lg p-4 mt-2">
                  <input type="checkbox" id="signatory_us" checked={editForm.is_authorized_signatory || false} 
                    onChange={(e) => setEditForm({...editForm, is_authorized_signatory: e.target.checked})}
                    className="w-5 h-5 rounded bg-slate-800 border-slate-600" />
                  <label htmlFor="signatory_us" className="text-sm text-slate-300">Authorized Signatory (can sign documents on behalf of company)</label>
                </div>
              </>
            ) : (
              <>
                {/* India-specific: Identity Details */}
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider pt-4">Identity Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">PAN Number</label>
                    <Input value={editForm.pan_number || ''} onChange={(e) => setEditForm({...editForm, pan_number: e.target.value.toUpperCase()})}
                      className="bg-slate-900 border-slate-700 text-white font-mono" placeholder="ABCDE1234F" maxLength={10} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Aadhaar Number</label>
                    <Input value={editForm.aadhaar_number || ''} onChange={(e) => setEditForm({...editForm, aadhaar_number: e.target.value})}
                      className="bg-slate-900 border-slate-700 text-white font-mono" placeholder="1234 5678 9012" maxLength={14} />
                  </div>
                </div>

                {/* India-specific: DIN & DSC Details */}
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider pt-4">KYC & DSC Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">DIN Number</label>
                    <Input value={editForm.din_number || ''} onChange={(e) => setEditForm({...editForm, din_number: e.target.value})}
                      className="bg-slate-900 border-slate-700 text-white font-mono" placeholder="12345678" maxLength={8} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">KYC Due Date (DIR-3 KYC)</label>
                    <Input type="date" value={editForm.kyc_expiry_date || ''} onChange={(e) => setEditForm({...editForm, kyc_expiry_date: e.target.value})}
                      className="bg-slate-900 border-slate-700 text-white" />
                    {editForm.kyc_expiry_date && (
                      <div className="mt-1"><ComputedStatusBadge expiryDate={editForm.kyc_expiry_date} label="KYC" /></div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">DSC Expiry Date</label>
                    <Input type="date" value={editForm.dsc_expiry_date || ''} onChange={(e) => setEditForm({...editForm, dsc_expiry_date: e.target.value})}
                      className="bg-slate-900 border-slate-700 text-white" />
                    {editForm.dsc_expiry_date && (
                      <div className="mt-1"><ComputedStatusBadge expiryDate={editForm.dsc_expiry_date} label="DSC" /></div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">DSC Class</label>
                    <select value={editForm.dsc_class || ''} onChange={(e) => setEditForm({...editForm, dsc_class: e.target.value})}
                      className="w-full rounded-md bg-slate-900 border-slate-700 text-white p-2 text-sm">
                      <option value="">Select Class</option>
                      <option value="class_2">Class 2</option>
                      <option value="class_3">Class 3</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">DSC Provider</label>
                    <Input value={editForm.dsc_provider || ''} onChange={(e) => setEditForm({...editForm, dsc_provider: e.target.value})}
                      className="bg-slate-900 border-slate-700 text-white" placeholder="e.g., eMudhra, Sify" />
                  </div>
                  <div className="col-span-2 flex items-center gap-3 bg-slate-900/50 rounded-lg p-4">
                    <input type="checkbox" id="signatory" checked={editForm.is_authorized_signatory || false} 
                      onChange={(e) => setEditForm({...editForm, is_authorized_signatory: e.target.checked})}
                      className="w-5 h-5 rounded bg-slate-800 border-slate-600" />
                    <label htmlFor="signatory" className="text-sm text-slate-300">Authorized Signatory (can sign documents on behalf of company)</label>
                  </div>
                </div>

                {/* India-specific: Additional Information */}
                <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider pt-4">Additional Information</h3>
                <div className="grid grid-cols-2 gap-4 bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Occupation</label>
                    <Input value={editForm.occupation || ''} onChange={(e) => setEditForm({...editForm, occupation: e.target.value})}
                      className="bg-slate-900 border-slate-700 text-white" placeholder="e.g., Business Owner, Engineer" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Nationality</label>
                    <Input value={editForm.nationality || ''} onChange={(e) => setEditForm({...editForm, nationality: e.target.value})}
                      className="bg-slate-900 border-slate-700 text-white" placeholder="e.g., Indian" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-slate-500 mb-1 block">Duration of Stay at Current Address</label>
                    <select 
                      value={editForm.duration_of_stay || ''} 
                      onChange={(e) => setEditForm({...editForm, duration_of_stay: e.target.value})}
                      className="w-full bg-slate-900 border border-slate-700 rounded-md p-2 text-white"
                    >
                      <option value="">Select duration</option>
                      <option value="less_than_1_year">Less than 1 year</option>
                      <option value="1_to_2_years">1-2 years</option>
                      <option value="2_to_5_years">2-5 years</option>
                      <option value="5_to_10_years">5-10 years</option>
                      <option value="more_than_10_years">More than 10 years</option>
                    </select>
                  </div>
                  {hasProfitShareCompany && (
                    <div className="col-span-2">
                      <label className="text-xs text-emerald-400 mb-1 block flex items-center gap-1">
                        Profit &amp; Loss Sharing Ratio (%)
                      </label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={editForm.profit_share_ratio ?? ''}
                        onChange={(e) => setEditForm({ ...editForm, profit_share_ratio: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white"
                        placeholder="e.g. 50"
                        data-testid="admin-director-profit-share-input"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">
                        Applies to LLP / Partnership partners. Leave blank if not applicable.
                      </p>
                    </div>
                  )}
                </div>

                {/* India-specific: V3 Credentials */}
                <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider pt-4">V3 Credentials</h3>
                <div className="grid grid-cols-2 gap-4 bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Username</label>
                    <Input value={editForm.v3_username || ''} onChange={(e) => setEditForm({...editForm, v3_username: e.target.value})}
                      className="bg-slate-900 border-slate-700 text-white" placeholder="V3 Username" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Password</label>
                    <Input type="text" value={editForm.v3_password || ''} onChange={(e) => setEditForm({...editForm, v3_password: e.target.value})}
                      className="bg-slate-900 border-slate-700 text-white" placeholder="V3 Password" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-slate-500 mb-1 block">Security Hint Question</label>
                    <Input value={editForm.v3_hint_question || ''} onChange={(e) => setEditForm({...editForm, v3_hint_question: e.target.value})}
                      className="bg-slate-900 border-slate-700 text-white" placeholder="Security hint question" />
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            {/* DISPLAY VIEW - Country-aware */}
            
            {/* Personal Information Section */}
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <DisplayField label="Full Name" value={director.full_name} hideIfEmpty={false} />
                <DisplayField label="Email" value={director.email} hideIfEmpty={false} />
                <DisplayField label="Phone" value={director.phone} />
                <DisplayField label="Date of Birth" value={director.date_of_birth} />
                {/* India-specific: Father's Name */}
                {!isUSDirector && <DisplayField label="Father's Name" value={director.father_name} hideIfEmpty={false} />}
                {/* US-specific: Ownership */}
                {isUSDirector && director.ownership_percentage && (
                  <div className="bg-slate-900/50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Ownership Percentage</p>
                    <p className="text-emerald-400 text-lg font-bold">{director.ownership_percentage}%</p>
                  </div>
                )}
                {director.address && (
                  <div className="col-span-2 bg-slate-900/50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1">Address</p>
                    <p className="text-white text-sm">{director.address}</p>
                  </div>
                )}
              </div>
            </div>

            {/* US-specific: Identity & Tax Details */}
            {isUSDirector ? (
              <>
                <div>
                  <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Identity & Tax Details</h3>
                  <div className="grid grid-cols-2 gap-4 bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                    {director.ssn && (
                      <div className="bg-slate-900/50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">SSN (Last 4)</p>
                        <p className="text-white text-sm font-mono">***-**-{director.ssn}</p>
                      </div>
                    )}
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">ITIN Assigned</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        director.itin_assigned ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'
                      }`}>
                        {director.itin_assigned ? 'Yes' : 'No'}
                      </span>
                    </div>
                    {director.itin && (
                      <DisplayField label="ITIN Number" value={director.itin} mono={true} />
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-3">Business & Compliance</h3>
                  <div className="grid grid-cols-2 gap-4 bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Compensation Method</p>
                      <p className="text-white text-sm capitalize">{director.compensation_method?.replace(/_/g, ' ') || '-'}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Visited US for Business</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        director.visited_us_for_business ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'
                      }`}>
                        {director.visited_us_for_business ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">W-8 Form Provided</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        director.w8_provided ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {director.w8_provided ? 'Yes' : 'Pending'}
                      </span>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Authorized Signatory</p>
                      <StatusBadge status={director.is_authorized_signatory} type="signatory" />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* India-specific: Identity Details Section */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Identity Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-3" data-testid="director-pan-display">
                      <p className="text-xs text-slate-500 mb-1">PAN Number</p>
                      <p className="text-white text-sm font-mono">
                        {director.pan_number ? `${director.pan_number.slice(0, 4)}${'*'.repeat(Math.max(0, director.pan_number.length - 5))}${director.pan_number.slice(-1)}` : 'Not provided'}
                      </p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3" data-testid="aadhaar-display">
                      <p className="text-xs text-slate-500 mb-1">Aadhaar Number</p>
                      <p className="text-white text-sm font-mono">
                        {director.aadhaar_number ? `XXXX XXXX ${director.aadhaar_number.slice(-4)}` : 'Not provided'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* India-specific: DIN, KYC & DSC Details Section */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">KYC & DSC Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">DIN Number</p>
                      <p className="text-white text-sm font-mono">{director.din_number || '-'}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3" data-testid="kyc-status-display">
                      <p className="text-xs text-slate-500 mb-1">KYC Status (DIR-3 KYC)</p>
                      <ComputedStatusBadge expiryDate={director.kyc_expiry_date} label="KYC" />
                      {director.kyc_expiry_date && (
                        <p className="text-xs text-slate-500 mt-1">Due: {new Date(director.kyc_expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      )}
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3" data-testid="dsc-status-display">
                      <p className="text-xs text-slate-500 mb-1">DSC Status</p>
                      <ComputedStatusBadge expiryDate={director.dsc_expiry_date || director.dsc_expiry} label="DSC" />
                      {(director.dsc_expiry_date || director.dsc_expiry) && (
                        <p className="text-xs text-slate-500 mt-1">Expires: {new Date(director.dsc_expiry_date || director.dsc_expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      )}
                      {(director.dsc_expiry_date || director.dsc_expiry) && (
                        <div className="mt-3 pt-3 border-t border-slate-700/60 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs text-slate-400 flex items-center gap-1.5">
                              {dscRemindersOn ? <Bell className="w-3 h-3 text-emerald-400" /> : <BellOff className="w-3 h-3 text-slate-500" />}
                              DSC Reminder Emails
                            </p>
                            <p className={`text-[11px] mt-0.5 ${dscRemindersOn ? 'text-emerald-400' : 'text-slate-500'}`}>
                              {dscRemindersOn ? 'Active — expiry alerts are being sent' : 'Stopped — no expiry alerts will be sent'}
                            </p>
                          </div>
                          {canManage && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={dscReminderBusy}
                              onClick={toggleDscReminders}
                              data-testid="toggle-dsc-reminders-btn"
                              className={dscRemindersOn
                                ? 'shrink-0 border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300'
                                : 'shrink-0 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300'}
                            >
                              {dscReminderBusy ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : dscRemindersOn ? (
                                <><BellOff className="w-3.5 h-3.5 mr-1" /> Stop</>
                              ) : (
                                <><Bell className="w-3.5 h-3.5 mr-1" /> Resume</>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <DisplayField label="DSC Class" value={director.dsc_class?.replace('_', ' ')} />
                    <DisplayField label="DSC Provider" value={director.dsc_provider} />
                    <div className="col-span-2 bg-slate-900/50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Authorized Signatory</p>
                      <StatusBadge status={director.is_authorized_signatory} type="signatory" />
                    </div>
                  </div>
                </div>

                {/* India-specific: Additional Information Section */}
                {(director.occupation || director.nationality || director.duration_of_stay || (hasProfitShareCompany && director.profit_share_ratio != null)) && (
                  <div>
                    <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Additional Information</h3>
                    <div className="grid grid-cols-2 gap-4 bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                      <DisplayField label="Occupation" value={director.occupation} />
                      <DisplayField label="Nationality" value={director.nationality} />
                      {director.duration_of_stay && (
                        <div className="col-span-2 bg-slate-900/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 mb-1">Duration of Stay at Current Address</p>
                          <p className="text-white text-sm">{director.duration_of_stay?.replace(/_/g, ' ')}</p>
                        </div>
                      )}
                      {hasProfitShareCompany && director.profit_share_ratio != null && (
                        <div
                          className="col-span-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3"
                          data-testid="admin-director-profit-share-display"
                        >
                          <p className="text-xs text-emerald-400 mb-1">Profit &amp; Loss Sharing Ratio</p>
                          <p className="text-emerald-300 text-lg font-bold">{director.profit_share_ratio}%</p>
                          <p className="text-[11px] text-slate-500 mt-1">
                            LLP / Partnership profit &amp; loss share
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* India-specific: V3 Credentials Section */}
                {(director.v3_username || director.v3_password || director.v3_hint_question) && (
                  <div>
                    <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      V3 Credentials
                    </h3>
                    <div className="grid grid-cols-2 gap-4 bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                      <DisplayField label="Username" value={director.v3_username} mono={true} />
                      <DisplayField label="Password" value={director.v3_password ? '••••••••' : null} />
                      {director.v3_hint_question && (
                        <div className="col-span-2 bg-slate-900/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 mb-1">Security Hint Question</p>
                          <p className="text-white text-sm">{director.v3_hint_question}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* External Credentials Section - Common */}
            <ExternalCredentials 
              entityType="director"
              entityId={director.id}
              entityName={director.full_name}
              isAdmin={true}
            />

            {/* Linked Companies - Common */}
            <div>
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                Linked Companies ({companies?.length || 0})
              </h3>
              {companies && companies.length > 0 ? (
                <div className="space-y-3">
                  {companies.map((company) => (
                    <div 
                      key={company.id} 
                      className={`bg-slate-900/50 rounded-lg p-4 border border-slate-700/30 ${onNavigateToCompany ? 'cursor-pointer hover:bg-slate-800 hover:border-blue-500/40 transition-colors' : ''}`}
                      onClick={onNavigateToCompany ? () => onNavigateToCompany(company) : undefined}
                      data-testid={`director-company-link-${company.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-semibold text-sm ${
                          company.country === 'US' || company.entity_type?.startsWith('us_')
                            ? 'bg-gradient-to-br from-red-500 to-blue-600'
                            : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                        }`}>
                          {company.company_name?.charAt(0)?.toUpperCase() || 'C'}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium ${onNavigateToCompany ? 'text-blue-400 hover:text-blue-300' : 'text-white'}`}>{company.company_name}</p>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              company.country === 'US' || company.entity_type?.startsWith('us_')
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-orange-500/20 text-orange-400'
                            }`}>
                              {company.country === 'US' || company.entity_type?.startsWith('us_') ? '🇺🇸' : '🇮🇳'}
                            </span>
                          </div>
                          <p className="text-slate-500 text-sm capitalize">{company.entity_type?.replace(/_/g, ' ')}</p>
                        </div>
                        {onNavigateToCompany && <ChevronRight className="w-4 h-4 text-slate-500" />}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">Not linked to any company</p>
              )}
            </div>

            {/* Director KYC Documents */}
            <KYCDocuments 
              entityType="director"
              entityId={director.id}
              entityName={director.full_name}
              isAdmin={true}
              onRefresh={onRefresh}
              country={isUSDirector ? 'US' : 'IN'}
            />

            {/* Last Updated Indicator */}
            <LastUpdatedBadge 
              updatedAt={director.updated_at}
              updatedByName={director.updated_by_name}
              updatedByRole={director.updated_by_role}
            />
          </>
        )}
      </div>
    </div>
  );
};

export const DirectorsTab = ({ directors, loading, onSelectDirector, selectedDirector, directorCompanies, currentUser, onRefresh, onNavigateToCompany, onNavigateToShareholder, onNavigateToOrder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('all'); // 'all', 'IN', 'US'

  // Helper to detect if director is from US
  const isUSDirectorFn = (director) => {
    return director?.country === 'US' || director?.ssn || director?.itin;
  };

  const filteredDirectors = directors.filter(director => {
    // Text search filter
    const matchesSearch = director.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      director.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      director.din_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      director.pan_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      director.ssn?.includes(searchTerm) ||
      director.itin?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Country filter
    const directorCountry = isUSDirectorFn(director) ? 'US' : 'IN';
    const matchesCountry = countryFilter === 'all' || directorCountry === countryFilter;
    
    return matchesSearch && matchesCountry;
  });

  // Count directors by country
  const countByCountry = {
    all: directors.length,
    IN: directors.filter(d => !isUSDirectorFn(d)).length,
    US: directors.filter(d => isUSDirectorFn(d)).length,
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Directors List */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search directors by name, email, DIN or PAN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Country Filter */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCountryFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              countryFilter === 'all' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            All ({countByCountry.all})
          </button>
          <button
            onClick={() => setCountryFilter('IN')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              countryFilter === 'IN' 
                ? 'bg-orange-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <span>🇮🇳</span> India ({countByCountry.IN})
          </button>
          <button
            onClick={() => setCountryFilter('US')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              countryFilter === 'US' 
                ? 'bg-blue-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <span>🇺🇸</span> USA ({countByCountry.US})
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-700"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-slate-700 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-slate-700 rounded w-48"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto pr-2">
            {filteredDirectors.map((director) => (
              <DirectorCard
                key={director.id}
                director={director}
                onClick={() => onSelectDirector(director)}
                isSelected={selectedDirector?.id === director.id}
              />
            ))}
            {filteredDirectors.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                {searchTerm ? `No directors found matching "${searchTerm}"` : 'No directors registered'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Director Details */}
      <DirectorDetailPanel
        director={selectedDirector}
        companies={directorCompanies || selectedDirector?.companies}
        currentUser={currentUser}
        onRefresh={onRefresh}
        onNavigateToCompany={onNavigateToCompany}
        onNavigateToShareholder={onNavigateToShareholder}
      />
    </div>
  );
};
